/* eslint-disable @typescript-eslint/no-explicit-any */
import { kvGet, kvSet } from './db';
import { atualizarItemFila, enfileirar, listarFila, buscarVendaNaFila, removerDaFila } from './fila';
import { baixarEstoqueLocal, buscarOperacaoPorId, buscarParceiroPorId, buscarProdutoPorId } from './cadastros';
import { sessaoAtivaLocal, sessaoOfflinePendente } from './sessaoOffline';

// Vendas feitas sem internet — do PDV ('venda', POST /vendaspdv) e da tela
// de vendas do FrontSIAC ('vendafront', POST /vendas). Vão pra fila e são
// enviadas depois com o mesmo idmov (ver sincronizacao.ts — o backend ignora
// reenvio de um idmov já gravado), junto com tudo que a tela precisa pra
// mostrar/imprimir a venda enquanto ela ainda não está no servidor.

const CHAVE_EMPRESA_IMPRESSAO = 'empresaImpressao';
const CHAVE_EMPRESA = 'empresa';

// Campos da empresa que os comprovantes usam — só eles ficam gravados no
// computador. /vendasimpressao devolve a linha inteira da tabela empresa
// (inclusive senha do certificado digital e CSC da NFC-e).
const CAMPOS_EMPRESA = ['idempresa', 'nome', 'fantasia', 'cnpj', 'ie', 'endereco', 'numero', 'bairro', 'cidade', 'uf', 'cep', 'telefone', 'logo'];

// Guardada pela sincronização (GET /empresaimpressao) e a cada impressão
// online (ver VendasService.Impressao).
export const guardarEmpresaImpressao = (empresa: any[]) => {
  if (!Array.isArray(empresa) || !empresa.length) return undefined;
  const somenteComprovante = empresa.map((linha) => Object.fromEntries(
    CAMPOS_EMPRESA.filter((campo) => linha?.[campo] !== undefined).map((campo) => [campo, linha[campo]]),
  ));
  return kvSet(CHAVE_EMPRESA_IMPRESSAO, somenteComprovante);
};

const empresaParaImpressao = async (): Promise<any[]> => {
  const completa = await kvGet<any[]>(CHAVE_EMPRESA_IMPRESSAO);
  if (completa?.length) return completa;
  const basica = await kvGet<any>(CHAVE_EMPRESA);
  return basica ? [basica] : [];
};

// Mesmo com internet, a venda precisa esperar na fila se depende de algo
// que ainda não chegou ao servidor: o caixa aberto offline (o backend
// vincula a venda do PDV à sessão aberta) ou um parceiro cadastrado offline.
export const vendaDependeDaFila = async (idparceiro: string, considerarCaixa = true): Promise<boolean> => {
  if (considerarCaixa && await sessaoOfflinePendente()) return true;
  const fila = await listarFila();
  return fila.some((item) => item.tipo === 'parceiro' && item.id === idparceiro);
};

// Mesmo formato de GET /vendasimpressao/:id.
const montarImpressao = async (dados: any, itensOriginais: any[]) => {
  const parceiro = await buscarParceiroPorId(dados.idparceiro);
  const sessao = await sessaoAtivaLocal();
  const itens = await Promise.all(itensOriginais.map(async (item: any) => {
    const produto = await buscarProdutoPorId(item.idprodutos);
    return {
      ...item,
      idmov: dados.idmov,
      descricao: item.descricao ?? produto?.descricao,
      un: item.un ?? produto?.un,
      ean: item.ean ?? produto?.ean,
      localizado: item.localizado ?? produto?.localizado,
      marca: item.marca ?? produto?.marca,
    };
  }));
  return {
    idmov: dados.idmov,
    dataoperacao: dados.emissao,
    // Número só existe depois que o servidor grava a venda — fica em branco.
    numero: '',
    total: dados.total,
    vprod: dados.vprod,
    desconto: dados.desconto,
    acrescimo: dados.acrescimo,
    obs: dados.obs,
    nome: parceiro?.nome,
    cpf: parceiro?.cpf,
    vendedor: sessao?.nomeUsuario || '',
    empresa: await empresaParaImpressao(),
    itens,
  };
};

// ---------- PDV ----------

export const salvarVendaOffline = async (
  idconta: string,
  corpo: any[],
  dados: any,
  itensOriginais: any[],
): Promise<string> => {
  const [entrada, itens] = corpo;
  const impressao = await montarImpressao(dados, itensOriginais);
  // Sessão de caixa em que a venda foi feita — a tela "Notas fiscais" do PDV
  // só mostra vendas da sessão atual (ver listarVendasPdvOffline).
  const sessao = await sessaoAtivaLocal();
  await enfileirar('venda', idconta, corpo, {
    impressao,
    total: entrada.total,
    emissao: entrada.emissao,
    nomeParceiro: impressao.nome || '',
    idsessao: sessao?.idsessao || '',
  }, dados.idmov);
  await baixarEstoqueLocal(itens);
  return dados.idmov;
};

// Vendas do PDV feitas offline e ainda não enviadas, de UMA sessão de caixa
// — tela "Notas fiscais" do PDV, como "não sincronizadas" (sem NFC-e ainda:
// a nota só pode ser emitida depois que o servidor tiver a venda).
export const listarVendasPdvOffline = async (idsessao: string) => {
  if (!idsessao) return [];
  const fila = await listarFila();
  return fila
    .filter((item) => item.tipo === 'venda' && item.extra?.idsessao === idsessao)
    .map((item) => ({
      idmov: item.id,
      emissao: item.extra?.emissao as string,
      total: Number(item.extra?.total) || 0,
      nomeParceiro: (item.extra?.nomeParceiro as string) || 'Consumidor',
      pagamentos: ((item.payload?.[0]?.formasPagamento || []) as { categoria?: string; valor: number }[])
        .map((forma) => ({ categoria: forma.categoria || '', valor: Number(forma.valor) || 0 })),
      // 'erro' = o servidor recusou ao sincronizar (motivo no campo erro).
      status: item.status,
      erro: item.erro,
    }));
};

export const impressaoVendaOffline = async (idmov: string) => {
  const item = await buscarVendaNaFila(idmov).catch(() => undefined);
  return item?.extra?.impressao;
};

// ---------- FrontSIAC (tela de vendas) ----------

// A venda como GET /vendas/:id devolve — pra tela de detalhe abrir a venda
// recém-gravada offline (ela navega pra /venda/:idmov logo depois de salvar).
const montarVendaFront = async (corpo: any[], itensOriginais: any[]) => {
  const [entrada] = corpo;
  const parceiro = await buscarParceiroPorId(entrada.idparceiro);
  return {
    ...entrada,
    numero: '',
    faturado: false,
    recebidas: 0,
    nome: parceiro?.nome || '',
    itens: itensOriginais.map((item) => ({ ...item, idmov: entrada.idmov })),
    offline: true,
  };
};

export const salvarVendaFrontOffline = async (corpo: any[], itensOriginais: any[]): Promise<string> => {
  const [entrada, itens] = corpo;
  const venda = await montarVendaFront(corpo, itensOriginais);
  await enfileirar('vendafront', '', corpo, {
    venda,
    impressao: await montarImpressao(entrada, itensOriginais),
    total: entrada.total,
    emissao: entrada.emissao,
    nomeParceiro: venda.nome,
  }, entrada.idmov);
  await baixarEstoqueLocal(itens);
  return entrada.idmov;
};

export const vendaFrontOffline = async (idmov: string) => {
  const item = await buscarVendaNaFila(idmov).catch(() => undefined);
  return item?.tipo === 'vendafront' ? item.extra?.venda : undefined;
};

// Alterar uma venda que ainda nem foi enviada é só trocar o que vai ser
// enviado (mesmo idmov) — vendas que já estão no servidor não podem ser
// alteradas offline.
export const atualizarVendaFrontOffline = async (corpo: any[], itensOriginais: any[]): Promise<boolean> => {
  const [entrada] = corpo;
  const item = await buscarVendaNaFila(entrada.idmov);
  if (!item || item.tipo !== 'vendafront') return false;
  const venda = await montarVendaFront(corpo, itensOriginais);
  await atualizarItemFila({
    ...item,
    payload: corpo,
    status: 'pendente',
    erro: undefined,
    extra: {
      ...item.extra,
      venda,
      impressao: await montarImpressao(entrada, itensOriginais),
      total: entrada.total,
      emissao: entrada.emissao,
      nomeParceiro: venda.nome,
    },
  });
  return true;
};

export const excluirVendaFrontOffline = async (idmov: string): Promise<boolean> => {
  const item = await buscarVendaNaFila(idmov);
  if (!item || item.tipo !== 'vendafront') return false;
  await removerDaFila(idmov);
  return true;
};

// Listagem de vendas offline: as que ainda estão na fila, no formato de
// GET /vendas (idmov, emissao, descricao da operação, cpf, nome, numero, total).
export const listarVendasFrontOffline = async () => {
  const fila = (await listarFila()).filter((item) => item.tipo === 'vendafront');
  return Promise.all(fila.map(async (item) => {
    const idoperacao = item.payload?.[0]?.idoperacao;
    const operacao = idoperacao ? await buscarOperacaoPorId(idoperacao).catch(() => undefined) : undefined;
    return {
      idmov: item.id,
      emissao: item.extra?.emissao,
      descricao: operacao?.descricao || '',
      cpf: item.extra?.impressao?.cpf || '',
      nome: item.extra?.nomeParceiro || '',
      numero: '',
      total: item.extra?.total || 0,
      idoperacao,
      idparceiro: item.payload?.[0]?.idparceiro || '',
      // Marcadores pra listagem diferenciar a venda que ainda não chegou ao
      // servidor ('pendente') ou que o servidor recusou ao sincronizar
      // ('erro', motivo em erroOffline) — ver ListagemDeVenda.tsx.
      offline: true as const,
      statusOffline: item.status,
      erroOffline: item.erro,
    };
  }));
};
